import { message as antdMessage } from 'antd';
import { REHYDRATE } from 'redux-persist';
import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

import { eventReasonClient, userPreferenceClient } from 'clients';
import { EVENT_REASON_PROMPT_ENTITY_ID, EVENT_REASON_SNOOZE_DURATION_MS } from 'constants/event-reason';
import { setMyPreferences } from 'store/modules/myPreferences/actions';
import { MyPreference } from 'store/modules/myPreferences/types';
import NetworkActionTypes from 'store/modules/network/constants';
import { selectNetworkOnline } from 'store/modules/network/selectors';
import { acknowledgeNotification } from 'store/modules/notifications/actions';
import NotificationsActionTypes from 'store/modules/notifications/constants';
import { makeSelectNotificationById } from 'store/modules/notifications/selectors';
import { EventReason } from 'types/models/event-reason';
import { EntityKind, EventReasonPromptPreference, UserPreference } from 'types/models/user-preference';
import { APIResponseParsed } from 'types/response';
import {
  buildEventReasonPromptContext,
  buildPendingEventReasonTaggingId,
  isEligibleForEventReasonPrompt,
  isEventReasonPromptSnoozed,
  partitionEventReasonsByTriggerType
} from 'utils/event-reason';

import {
  closeEventReasonPrompt,
  fetchEventReasonCatalogFailure,
  fetchEventReasonCatalogSuccess,
  openEventReasonPrompt,
  removePendingEventReasonPromptSnooze,
  removePendingEventReasonTagging,
  setEventReasonCatalog,
  setPendingEventReasonPromptSnooze,
  setPendingEventReasonTagging,
  skipEventReasonTagging,
  submitEventReasonTagging
} from './actions';
import ActionTypes from './constants';
import {
  selectEffectiveReasonPromptSnooze,
  selectEventReasonPromptContext,
  selectIsEventReasonCatalogLoaded,
  selectPendingEventReasonTaggingsAsArray,
  selectPendingReasonPromptSnooze
} from './selectors';
import { PendingEventReasonTagging } from './types';

function* savePromptSnoozePreference(): SagaIterator {
  const snoozedUntil = Date.now() + EVENT_REASON_SNOOZE_DURATION_MS;
  const preference: EventReasonPromptPreference = { snoozedUntil };
  const optimisticPreference: MyPreference = {
    entityKind: EntityKind.EVENT_REASON_PROMPT,
    entityId: EVENT_REASON_PROMPT_ENTITY_ID,
    preference
  };

  yield put(setPendingEventReasonPromptSnooze(preference));
  yield put(setMyPreferences([optimisticPreference]));
  antdMessage.success('Pop up is muted for 7 days');
}

function* processPendingSnooze(): SagaIterator {
  const pendingSnooze: ReturnType<typeof selectPendingReasonPromptSnooze> = yield select(
    selectPendingReasonPromptSnooze
  );
  if (!pendingSnooze) {
    return;
  }

  const response: APIResponseParsed<UserPreference> = yield call(userPreferenceClient.editPreference, {
    entityKind: EntityKind.EVENT_REASON_PROMPT,
    entityId: EVENT_REASON_PROMPT_ENTITY_ID,
    preference: pendingSnooze
  });
  if (response.data) {
    yield put(setMyPreferences([response.data]));
    yield put(removePendingEventReasonPromptSnooze());
  } else if (response.error.isNetworkError) {
    return;
  } else {
    yield put(removePendingEventReasonPromptSnooze());
  }
}

function* requestFetchEventReasonCatalog(): SagaIterator {
  const response: APIResponseParsed<EventReason[]> = yield call(eventReasonClient.fetchEventReasons);
  if (response.data) {
    yield all([
      put(setEventReasonCatalog(partitionEventReasonsByTriggerType(response.data))),
      put(fetchEventReasonCatalogSuccess())
    ]);
  } else {
    const errorMessage = response.error?.message || 'Sorry, something went wrong.';
    yield put(fetchEventReasonCatalogFailure(errorMessage));
  }
}

function* openEventReasonPromptOnAck(
  action: ReturnType<typeof acknowledgeNotification>
): SagaIterator {
  const isCatalogLoaded: ReturnType<typeof selectIsEventReasonCatalogLoaded> = yield select(
    selectIsEventReasonCatalogLoaded
  );
  if (!isCatalogLoaded) {
    return;
  }

  const snoozePreference: ReturnType<typeof selectEffectiveReasonPromptSnooze> = yield select(
    selectEffectiveReasonPromptSnooze
  );
  if (isEventReasonPromptSnoozed(snoozePreference)) {
    return;
  }

  const existingPrompt: ReturnType<typeof selectEventReasonPromptContext> = yield select(
    selectEventReasonPromptContext
  );
  if (existingPrompt) {
    return; // Don't open a second one.
  }

  const { notificationId } = action.payload;
  const notification: ReturnType<ReturnType<typeof makeSelectNotificationById>> = yield select(
    makeSelectNotificationById(notificationId)
  );
  if (!notification || !isEligibleForEventReasonPrompt(notification)) {
    return;
  }

  yield put(openEventReasonPrompt(buildEventReasonPromptContext(notification)));
}

function* requestSkipEventReasonTagging(
  action: ReturnType<typeof skipEventReasonTagging>
): SagaIterator {
  const { shouldSnooze } = action.payload;
  if (shouldSnooze) {
    yield call(savePromptSnoozePreference);
  }
  yield put(closeEventReasonPrompt());
}

function* requestSubmitEventReasonTagging(
  action: ReturnType<typeof submitEventReasonTagging>
): SagaIterator {
  const {
    reasonCodes,
    freeText,
    shouldSnooze
  } = action.payload;

  const promptContext: ReturnType<typeof selectEventReasonPromptContext> = yield select(
    selectEventReasonPromptContext
  );
  if (!promptContext) {
    return;
  }

  if (shouldSnooze) {
    yield call(savePromptSnoozePreference);
  }

  const pendingTagging: PendingEventReasonTagging = {
    id: buildPendingEventReasonTaggingId(promptContext),
    promptContext,
    reasonCodes,
    freeText,
    createdAt: Date.now()
  };
  yield put(setPendingEventReasonTagging(pendingTagging));
  yield put(closeEventReasonPrompt());
}

function* processPendingTaggings(): SagaIterator {
  const pendingTaggings: PendingEventReasonTagging[] = yield select(
    selectPendingEventReasonTaggingsAsArray
  );

  for (const pendingTagging of pendingTaggings) {
    const { promptContext, reasonCodes, freeText, createdAt } = pendingTagging;
    const response: APIResponseParsed<'OK'> = yield call(eventReasonClient.createEventReasonTagging, {
      eventType: promptContext.eventType,
      eventId: promptContext.eventId,
      reasonCodes,
      freeText,
      createdAt: createdAt ?? Date.now()
    });

    if (response.data) {
      yield put(removePendingEventReasonTagging(pendingTagging.id));
      continue;
    }

    if (response.error.isNetworkError) {
      return;
    }

    yield put(removePendingEventReasonTagging(pendingTagging.id));
  }
}

function* processReasonPromptUpdateQueue(): SagaIterator {
  const networkOnline: ReturnType<typeof selectNetworkOnline> = yield select(selectNetworkOnline);
  if (!networkOnline) {
    return;
  }

  yield call(processPendingSnooze);
  yield call(processPendingTaggings);
}

function* watchFetchEventReasonCatalogRequest() {
  yield takeLatest(ActionTypes.FETCH_EVENT_REASON_CATALOG_REQUEST, requestFetchEventReasonCatalog);
}

function* watchOpenEventReasonPromptOnAck() {
  yield takeLatest(
    NotificationsActionTypes.SET_PENDING_ACKNOWLEDGEMENT,
    openEventReasonPromptOnAck
  );
}

function* watchSetPendingReasonPromptChanges() {
  yield takeLatest(
    [
      REHYDRATE,
      NetworkActionTypes.SET_NETWORK_ONLINE,
      ActionTypes.SET_PENDING_EVENT_REASON_TAGGING,
      ActionTypes.SET_PENDING_EVENT_REASON_PROMPT_SNOOZE
    ],
    processReasonPromptUpdateQueue
  );
}

function* watchSkipEventReasonTagging() {
  yield takeLatest(ActionTypes.SKIP_EVENT_REASON_TAGGING, requestSkipEventReasonTagging);
}

function* watchSubmitEventReasonTagging() {
  yield takeLatest(ActionTypes.SUBMIT_EVENT_REASON_TAGGING, requestSubmitEventReasonTagging);
}

export default function* eventReasonsSaga() {
  yield all([
    fork(watchFetchEventReasonCatalogRequest),
    fork(watchOpenEventReasonPromptOnAck),
    fork(watchSetPendingReasonPromptChanges),
    fork(watchSkipEventReasonTagging),
    fork(watchSubmitEventReasonTagging)
  ]);
}
